import { ImagineUIMessage } from "@imagine/ai-service/shared-types";
import {
  ConsoleLogMessage,
  NetworkRequestData,
} from "@imagine/sandbox-instrumentation";
import {
  isNetworkError,
  sanitizeNetworkLog,
} from "@imagine/sandbox-instrumentation/server";
import { useMutation } from "@tanstack/react-query";

import { toast } from "sonner";
import { imagineStore } from "@/stores/imagine.store";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { SendMessageResult } from "@/providers/imagine-chat-provider";
import { imagineConfigStore } from "@/stores/imagine-config.store";
import { HttpError } from "@/lib/http-error";

type SubmitChatMessageParams = {
  projectId: string;
  message: ImagineUIMessage;
  promptLabel?: string;
  trigger?: string;
  consoleLogs?: ConsoleLogMessage[];
  networkLogs?: Array<Partial<NetworkRequestData>>;
};

const MAX_CONSOLE_LOGS_TO_SEND = 50;
const MAX_NETWORK_ERRORS_TO_SEND = 50;

export function useSubmitChatMessageMutation({
  onError,
}: {
  onError?: (error: Error) => void;
} = {}) {
  const callbacks = imagineConfigStore.use.callbacks();

  const recentConsoleLogs = [...imagineStore.getState().consoleLogs.logs]
    .slice(-MAX_CONSOLE_LOGS_TO_SEND)
    .map((log) => ({ ...log })) as Array<ConsoleLogMessage>;
  const recentNetworkErrors = imagineStore
    .getState()
    .networkLogs.logs.filter(({ request }) => isNetworkError(request))
    .slice(-MAX_NETWORK_ERRORS_TO_SEND)
    .map((entry) => sanitizeNetworkLog(entry.request));

  return useMutation({
    mutationFn: async ({
      projectId,
      message,
      trigger = "none",
      promptLabel = "production",
      consoleLogs = recentConsoleLogs,
      networkLogs = recentNetworkErrors,
    }: SubmitChatMessageParams) => {
      const response = await aiServiceFetch(
        "/api/chat",
        {
          method: "POST",
          body: JSON.stringify({
            id: projectId,
            projectId,
            message,
            trigger,
            promptLabel,
            consoleLogs,
            networkLogs,
          }),
        },
        true,
      );

      if (!response.ok) {
        let errorData: any = null;

        try {
          errorData = await response.json();
        } catch (e) {
          throw await HttpError.fromResponse(response);
        }

        // Response is JSON, attach it to the error
        const isBillingError = response.status === 402;
        if (isBillingError) {
          throw new Error("You have run out of free credits.", {
            cause: {
              type: "billing_error",
              status: response.status,
            },
          });
        }

        throw await HttpError.fromResponse(response);
      }

      const json = await response.json();
      return json as SendMessageResult;
    },
    onError: (error) => {
      onError?.(error as Error);
      const errorData = (error as any).data;
      let errorText: string | undefined;

      const isBillingError = (error as any).cause?.type === "billing_error";
      if (isBillingError) {
        toast.error("You have run out of free credits.", {
          duration: Infinity,
          action: {
            label: "Upgrade",
            onClick: async () => {
              try {
                await callbacks?.onUpgrade?.();
              } catch (upgradeError) {
                console.error("Upgrade callback failed:", upgradeError);
                // Optionally show a toast about the upgrade failure
                toast.error(
                  "Failed to initiate upgrade. Please try again or contact support.",
                );
              }
            },
          },
        });
        return;
      }

      if (errorData) {
        // We have structured error data from the API
        if (errorData.issues) {
          const combined = errorData.issues
            .map((issue: any) => issue.message)
            .join("\n");
          errorText = combined;
        }
      }

      if (!errorText) {
        errorText = error.message;
      }

      toast.error(errorText);
    },
  });
}
